// Reconstructable activity log — in-memory action breadcrumb ring buffer.
// (TASK-398 observability.)
//
// WHY: A TITS dispatcher saved a job to the wrong customer and couldn't recall
// the steps. The final-state audit row records the RESULT ("job ended up on
// customer Y") but not the PATH (which job they opened, that the customer field
// started as A and they changed it to B, that they hit save). This buffer
// captures the *sequence* of high-signal UI actions so support can replay it.
//
// HARD CONSTRAINT (Chris): must NOT slow the app. So:
//   - record() is a synchronous array push — zero network, zero await, no JSON.
//     It touches nothing but a capped JS array. This is the only thing that runs
//     on the hot path (every dropdown change / form open).
//   - The buffer flushes to Firestore as ONE doc ONLY on a meaningful event:
//     a save or an error. No per-keystroke writes. (A future "report a problem"
//     affordance can flush with trigger:'user_report' — deferred, design Slice 4.)
//   - Flush reuses the existing audit sink: window.logAudit('telemetry', ...).
//     The 'telemetry' category gets a 90-day expiresAt → Firestore native TTL
//     auto-deletes it. We do NOT build a parallel writer (Pitfall #1 / the
//     "use the repo's primitive" rule). No firebase-config import here.
//
// PRIVACY: we record entity IDs (customerId, jobId), field-name + from/to for
// STRUCTURAL fields only (customer, date), and button clicks. NEVER free-text
// values (notes, POC name/phone/email, titles). We log *that* a field changed,
// never the typed string.

const MAX = 40;              // ring buffer cap — ~2-3 open→edit→save cycles
const sessionId = (() => {
  try { return crypto.randomUUID(); }
  catch { return 'sess_' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8); }
})();
const sessionStart = Date.now();

let buffer = [];

// record(action, detail) — synchronous, hot-path safe. Pushes one breadcrumb;
// trims oldest when over MAX. No await, no network, no serialization.
function record(action, detail) {
  try {
    buffer.push({ t: Date.now() - sessionStart, action, detail: detail || {} });
    if (buffer.length > MAX) buffer.shift();
  } catch (_) { /* never let instrumentation break the app */ }
}

// flush(trigger, extra) — the ONLY place this module touches the network.
// Snapshots the current buffer into ONE telemetry doc (90-day TTL), then clears.
// Fire-and-forget: we do NOT await it from the caller's hot path.
function flush(trigger, extra = {}) {
  try {
    if (!buffer.length) return;              // nothing to report → no write
    if (typeof window.logAudit !== 'function') return;
    if (window.AppState?.tenant === 'demo') { buffer = []; return; } // no demo noise
    const trail = buffer.slice();            // snapshot ≤40 entries
    buffer = [];                             // clear so the next flush is independent
    // jobId anchor at top level so tech-support can query
    //   auditLog where action=='action_trail' and jobId==X  → replay the steps.
    // NOTE: `...extra` is spread FIRST so the trusted, locally-controlled fields
    // (trail/sessionId/trigger/jobId) always win and cannot be overridden by a
    // caller-supplied `trail`/`sessionId` — closes a buffer-override / cap-bypass
    // vector (COD Adversary). `extra` may still add its own keys (e.g. jobId).
    window.logAudit('telemetry', 'action_trail', {
      type: 'action_trail',
      ...extra,
      jobId: extra.jobId || null,
      sessionId,
      trigger,
      trail,
    });
  } catch (_) { /* swallow — diagnostics must never break the app */ }
}

window.ActionLog = { record, flush, sessionId, get _size() { return buffer.length; } };
